import type { OnInit } from '@angular/core'
import { Component, inject, signal } from '@angular/core'

import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { distinctUntilChanged, of } from 'rxjs'
import { catchError, map, startWith, switchMap } from 'rxjs/operators'

import type { FAQResponseDto } from '../../../../api/responses'
import { FAQService } from '../../../../services/faq/faq.service'
import { FiltersComponent } from '../../components/help/filters/filters.component'
import { QuestionsSectionComponent } from '../../components/help/questions-section/questions-section.component'

interface Question {
  question: string
  answer: string
  position: number
  isOpen: boolean
}
interface Category {
  name: string
  position: number
  questions: Question[]
}

@Component({
  selector: 'dhp-help-page',
  templateUrl: './help-page.component.html',
  styleUrls: ['./help-page.component.scss'],
  imports: [FiltersComponent, QuestionsSectionComponent, TranslatePipe],
})
export class HelpPageComponent implements OnInit {
  private faqService = inject(FAQService)
  private translateService = inject(TranslateService)

  readonly categories = signal<Category[]>([])

  readonly selectedCategory = signal<Category | null>(null)

  selectCategory(name: string | null): void {
    this.selectedCategory.set(name ? this.getCategory(name) : null)
  }

  getCategory(categoryName: string): Category | null {
    return this.categories().find(c => c.name === categoryName) || null
  }

  getCategoryNames(): string[] {
    return this.categories().map(c => c.name)
  }

  ngOnInit(): void {
    this.translateService.onLangChange
      .pipe(
        startWith({ lang: this.translateService.getCurrentLang() }),
        map(e => e.lang ?? ''),
        distinctUntilChanged(), // only ignore same lang
        switchMap(lang => {
          return this.faqService.getAllFAQs(lang).pipe(
            catchError((error: unknown) => {
              console.error('Error fetching FAQs:', error)
              return of({} as FAQResponseDto)
            })
          )
        })
      )
      .subscribe(response => {
        const mapped = response.FaqCategories.docs.map(category => ({
          name: category.name,
          position: category.position,
          questions: category.questions.docs
            .map(q => ({
              question: q.question,
              answer: q.answer,
              position: q.position,
              isOpen: false,
            }))
            .sort((a, b) => a.position - b.position),
        }))
        mapped.sort((a, b) => a.position - b.position)
        this.categories.set(mapped)
      })
  }
}
